import { createAction } from '@reduxjs/toolkit'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import { getHiddenAndVisibleGroups } from '../utils/getHiddenAndVisibleGroups'
import { AIMaterial, SortedSuggestion } from '../../../models/aiClassifications'
import { isNonAreaJoistLine } from '../../../utils/project/project-helper-functions'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Workspace } from '../../lib/toolBoxes/2D'
import { selectHiddenAIIds, updateAISuggestionsVisibility } from '../../slices/2D'
import { ActiveGeometryGroup, selectActiveGeometryGroup } from '../../slices/geometry'

export function getAiIDs(groupToToggle: AIMaterial | SortedSuggestion | SortedSuggestion[]): string[] {
    if (Array.isArray((groupToToggle as AIMaterial).aiMaterials)) {
        return (groupToToggle as AIMaterial).aiMaterials.map((material) => material.id)
    }
    if (Array.isArray((groupToToggle as SortedSuggestion).settingsGroups)) {
        return (groupToToggle as SortedSuggestion).settingsGroups.flatMap((settingsGroup) =>
            settingsGroup.aiMaterials.map((material) => material.id)
        )
    }
    if (Array.isArray(groupToToggle) && groupToToggle[0].settingsGroups) {
        return groupToToggle.flatMap((group) =>
            group.settingsGroups.flatMap((settingsGroup) => settingsGroup.aiMaterials.map((material) => material.id))
        )
    }

    return []
}

export const toggleAIMaterialVisibilityAction = createAction<{
    visibility: boolean
    groupToToggle: AIMaterial | SortedSuggestion | SortedSuggestion[]
}>('toggleAIMaterialVisibilityAction')

export function* toggleAIMaterialVisibility(aiMaterialGroupPayload: {
    type: string
    payload: {
        visibility: boolean
        groupToToggle: AIMaterial | SortedSuggestion | SortedSuggestion[]
    }
}) {
    const { groupToToggle, visibility } = aiMaterialGroupPayload.payload

    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const activeGeometryGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)

    const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

    const aiIDs: string[] = getAiIDs(groupToToggle)

    yield put(updateAISuggestionsVisibility({ aiIDs, isVisible: visibility }))

    const aiIDsToHide = yield select(selectHiddenAIIds)

    const { hiddenDrawableGroupIds } = yield call(getHiddenAndVisibleGroups)

    const visibilityTest = (item: paper.Item): boolean => {
        if (
            activeGeometryGroup?.id &&
            item.data.opening_group_id &&
            activeGeometryGroup.id === item.data.opening_group_id
        ) {
            return true
        }

        return (
            !aiIDsToHide.includes(item.data?.aiSuggestion?.id) &&
            !(hiddenDrawableGroupIds.includes(item.data.opening_group_id) || isNonAreaJoistLine(item))
        )
    }

    yield call(workspaceTool.toggleDrawablesWithCondition, visibilityTest)
    yield call(workspaceTool.toggleModificationsWithCondition, visibilityTest)
}
export function* watchForToggleAIMaterialVisibility() {
    yield takeEvery(toggleAIMaterialVisibilityAction.type, toggleAIMaterialVisibility)
}
